import { Bouton } from './composants/bouton'
import { Badge } from './composants/badge'
import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useState } from 'react'
import { useRole } from '../lib/compte'
import { rafraichirTransmissions, transmettreDocument } from '../db/sync'
import { LIBELLES_STATUT_PLATEFORME, lireTransmissions, suiviEnCours, type Transmission as TransmissionConnue } from '../lib/transmissions'
import { getBase } from '../outbox/base'
import { ErreurFinalisation } from '../outbox/synchro'


const MESSAGES: Record<string, string> = {
  hors_ligne: 'La transmission à la plateforme demande du réseau.',
  non_connecte: 'Connectez-vous (menu Compte) pour transmettre la facture.',
  abonnement_requis: 'Abonnement de votre entreprise expiré : cette action est indisponible.',
  plateforme_non_configuree: 'Aucune plateforme de facturation n’est encore configurée sur le serveur.',
  plateforme_indisponible: 'La plateforme est indisponible. Réessayez plus tard : rien n’a été transmis.',
  facturx_invalide: 'Le fichier de facture électronique est illisible.',
  facturx_incoherent: 'Le fichier ne correspond pas à ce document (numéro ou type).',
  transmission_en_cours: 'Une transmission de ce document est déjà en cours.',
  non_finalise: 'Ce document n’est pas encore finalisé.',
  introuvable: 'Ce document n’existe pas sur le serveur : synchronisez puis réessayez.',
}

/** Raison d'un échec de fabrication du Factur-X, affichée telle quelle. */
export class ErreurFabrication extends Error {}

const dateHeureFr = (iso: string) => new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })

/** Transmissions connues sur cet appareil, mises à jour en direct ; relit les statuts chez la plateforme à l'ouverture si certains sont en cours. */
export function useTransmissions(): Record<string, TransmissionConnue> {
  const base = getBase()
  const connues = useLiveQuery(() => lireTransmissions(base), [], {} as Record<string, TransmissionConnue>)
  const enCours = suiviEnCours(connues)
  useEffect(() => {
    void rafraichirTransmissions(enCours)
    // Une seule relecture à l'ouverture de l'écran ; la suite passe par « Actualiser »
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return connues
}

/**
 * Transmission d'une facture, d'une situation ou d'un avoir finalisé à la plateforme agréée : bouton tant qu'elle
 * n'a pas eu lieu, ensuite statut chez la plateforme. `produire` fabrique le Factur-X sur l'appareil et lève une
 * `ErreurFabrication` si le document n'est pas prêt (réglages entreprise, client manquants…).
 */
export function Transmission(props: { documentId: string; libelle: string; connues: Record<string, TransmissionConnue>; produire: () => Promise<Blob> }) {
  const t = props.connues[props.documentId]
  const role = useRole()
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  async function agir(action: () => Promise<unknown>, repli: string) {
    setOccupe(true)
    setErreur(null)
    try {
      await action()
    } catch (e) {
      setErreur(e instanceof ErreurFabrication ? e.message : e instanceof ErreurFinalisation ? (MESSAGES[e.code] ?? repli) : repli)
    } finally {
      setOccupe(false)
    }
  }

  const transmettre = () => {
    if (!confirm(`Transmettre ${props.libelle} à la plateforme agréée ? Elle l’enverra au client : l’envoi est définitif.`)) return
    return agir(async () => transmettreDocument(props.documentId, await props.produire()), 'La transmission a échoué. Réessayez avec du réseau : la facture n’est déposée qu’une seule fois.')
  }

  return (
    <div className="flex flex-col gap-1 text-sm">
      {t ? (
        <>
          <Badge statut={t.statut === 'rejetee' ? 'danger' : 'succes'} className="self-start">
            {LIBELLES_STATUT_PLATEFORME[t.statut]} · transmise le {dateHeureFr(t.transmisLe)}
          </Badge>
          {t.statut !== 'rejetee' && t.statut !== 'mise_a_disposition' && (
            <Bouton variante="lien" className="self-start" onClick={() => agir(() => rafraichirTransmissions(true), 'Impossible de lire le statut.')} disabled={occupe}>
              {occupe ? 'Lecture…' : 'Actualiser le statut'}
            </Bouton>
          )}
        </>
      ) : role === 'membre' ? (
        <p className="text-slate-700">La transmission à la plateforme est réservée au propriétaire.</p>
      ) : (
        <Bouton variante="secondaire" className="self-start" onClick={transmettre} disabled={occupe}>
          {occupe ? 'Transmission…' : `Transmettre ${props.libelle} à la plateforme (réseau)`}
        </Bouton>
      )}
      {erreur && (
        <p className="rounded border-2 border-red-700 bg-red-50 p-2 text-red-900" role="alert">
          {erreur}
        </p>
      )}
    </div>
  )
}
